import { artElement, escapeHtml } from './activities/scene';
import { subtemas } from './content/binatang-peliharaan';
import { readHalaman } from './runtime/kelas';
import { readCompleted } from './runtime/progress';

/**
 * The gallery - the page a teacher lands on.
 *
 * Structured the way the curriculum is: a subtema is a week, and its cards are
 * the days of that week, each standing on its own patch of the same lawn the
 * lessons are built on, with the day's animal on it. The week reads left to
 * right as cat, dog, rabbit; the yard the class is filling is counted at the
 * top, because it is the one thing that carries from day to day.
 *
 * Unbuilt days are shown, dashed, rather than hidden - someone being shown this
 * should see the shape of a full week and how much of it is real.
 */

export function renderGallery(host: HTMLElement): () => void {
  let disposed = false;
  const halaman = readHalaman();

  const sections = subtemas
    .map((subtema) => {
      const cards = subtema.modules
        .map((module) => {
          const ready = module.status === 'ready';
          const done = ready ? readCompleted(module.id).size : 0;
          const total = 16;
          const tag = ready ? 'a' : 'div';
          const href = ready ? ` href="#/belajar/${module.id}"` : '';

          return `
            <${tag} class="lesson lesson--${module.status}"${href} data-hewan="${escapeHtml(module.hewan ?? '')}">
              <div class="lesson__scene" data-art="${module.hewan ? `${module.hewan}/utuh` : ''}">
                <span class="lesson__day">Hari ${module.hari}</span>
              </div>
              <div class="lesson__body">
                <h3 class="lesson__title">${escapeHtml(module.title)}</h3>
                ${module.nilai ? `<p class="lesson__nilai">Nilai: ${escapeHtml(module.nilai)}</p>` : ''}
                <p class="lesson__summary">${escapeHtml(module.summary)}</p>
                <div class="lesson__foot">
                  ${
                    ready
                      ? `<span class="lesson__meta">16 layar &middot; jalur inti ${module.jalur?.inti.length ?? 8}</span>
                         <span class="lesson__progress" style="--p:${Math.min(1, done / total)}" aria-label="${done} layar selesai"><i></i></span>
                         <span class="lesson__state">${done > 0 ? 'Lanjutkan' : 'Mulai'} &rarr;</span>`
                      : '<span class="lesson__state lesson__state--planned">Segera hadir</span>'
                  }
                </div>
              </div>
            </${tag}>`;
        })
        .join('');

      return `
        <section class="subtema">
          <header class="subtema__head">
            <p class="subtema__tema">${escapeHtml(subtema.tema)} &middot; Minggu ${subtema.minggu}</p>
            <h2>${escapeHtml(subtema.subtema)}</h2>
            <p class="subtema__jenjang">${escapeHtml(subtema.jenjang)}</p>
          </header>
          <div class="lessons">${cards}</div>
        </section>`;
    })
    .join('');

  host.innerHTML = `
    <div class="gallery">
      <header class="gallery__head">
        <a class="gallery__back" href="#/">&larr; Prototypes</a>
        <div class="gallery__title">
          <div>
            <h1>Belajar Bersama</h1>
            <p>Pelajaran untuk papan interaktif kelas. Pilih hari, atur jalurnya, lalu mulai.</p>
          </div>
          <div class="gallery__yard">
            <strong>${halaman.length}</strong>
            <span>binatang di<br />Halaman Kita</span>
          </div>
        </div>
      </header>
      ${sections}
    </div>
  `;

  // The day's animal on each card. Filled in after first paint: the page is
  // usable before the drawings arrive, and a drawing that fails costs a
  // picture, not the gallery.
  host.querySelectorAll<HTMLElement>('.lesson__scene[data-art]').forEach((scene) => {
    const id = scene.dataset['art'];
    if (!id) return;
    void artElement(id, 'art lesson__art').then((art) => {
      if (!disposed) scene.append(art);
    });
  });

  return () => {
    disposed = true;
  };
}
